import { createApp } from "vue";
import { createPinia } from "pinia";
import * as Sentry from "@sentry/vue";
import PrimeVue from "primevue/config";
import ToastService from "primevue/toastservice";
import ConfirmationService from "primevue/confirmationservice";
import Tooltip from "primevue/tooltip";
import { installAnalytics } from "./helpers/analytics.js";
import { autoAnimatePlugin } from "@formkit/auto-animate/vue";
import router from "./router/router.js";
import themePreset from "./theme/preset.js";

import "./style.css";
import App from "./App.vue";
import { createI18nInstance, createTranslationFormatPlugin, setAppLocale } from "./helpers/useI18n.js";
import { setSessionExpiredHandler } from "./helpers/useFetch.js";
import { listenForOrganizationChanges } from "./helpers/useBroadcast.js";

listenForOrganizationChanges();

const VITE_PRELOAD_RELOAD_KEY = `vite-preload-reload:${import.meta.url}`;

window.addEventListener("vite:preloadError", (event) => {
  if (sessionStorage.getItem(VITE_PRELOAD_RELOAD_KEY)) {
    return;
  }

  event.preventDefault();
  sessionStorage.setItem(VITE_PRELOAD_RELOAD_KEY, "1");
  window.location.reload();
});

const pinia = createPinia();
const app = createApp(App);

setSessionExpiredHandler((sessionExpiredRoute) => {
  if (
    router.currentRoute.value.path !== sessionExpiredRoute.path ||
    router.currentRoute.value.query.redirect_to !== sessionExpiredRoute.query.redirect_to
  ) {
    router.push(sessionExpiredRoute).catch(() => null);
  }
});

const initialLanguage = localStorage.getItem("language") || "en";
const i18n = createI18nInstance(initialLanguage);

Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [/^https:\/\/portal.dubrink.com/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

app.use(PrimeVue, {
  theme: {
    preset: themePreset,
    options: {
      prefix: "",
      darkModeSelector: false,
      cssLayer: false,
    },
  },
});
app.use(ToastService);
app.use(ConfirmationService);
app.use(autoAnimatePlugin);
app.use(router);
app.use(pinia);
app.use(i18n);
installAnalytics(app, router, { id: import.meta.env.VITE_GA_MEASUREMENT_ID });
app.use(createTranslationFormatPlugin(i18n), { allowedTags: ["b", "i"] });
app.directive("tooltip", Tooltip);

const bootstrap = async () => {
  await setAppLocale(i18n, initialLanguage);
  app.mount("#app");
};

bootstrap();
